
// Regular expression patterns for testing content-type response headers.
var RE_CONTENT_TYPE_JSON = new RegExp( "^application/(json|x-json|[\\w.-]+\\+json)\\b", "i" );
var RE_CONTENT_TYPE_TEXT = new RegExp( "^text/", "i" );
// Static strings.
var UNEXPECTED_ERROR_MESSAGE = "An unexpected error occurred while processing your request.";

/**
* This Error class contains diagnostic information about the underlying API request. The
* `userMessage` is the only value that should be considered safe to render for the user.
*/
export class ApiClientError extends Error {

	/**
	* I initialize the API client error.
	*/
	constructor( config ) {

		var type = ( config.type ?? "ServerError" );
		var domainType = ( config.domainType ?? "" );
		var status = ( config.status ?? 0 );

		var message = domainType
			? `${ domainType } (${ status })`
			: `${ type } (${ status })`
		;

		super( message, { cause: config.cause } );
		this.type = type;
		this.domainType = domainType;
		this.name = "ApiClientError";
		this.userMessage = ( config.userMessage || UNEXPECTED_ERROR_MESSAGE );
		this.status = status;
		this.attempts = 1;

	}

}


/**
* This API Client class provides a simplified API for making HTTP requests to the server.
* It also guarantees that any errors raised during the request and response processing are
* of type ApiClientError.
*/
export class ApiClient {

	/**
	* I make the API request with the given configuration options. Note that some options
	* are ignored / overwritten based on the type of request.
	*
	* GUARANTEE: All errors produced by this method will be instances of ApiClientError;
	* and will have a consistent structure even if they are low-level networking errors.
	*/
	async makeRequest( config ) {

		// All of the fetch preamble runs in its own try/catch so that we can clearly
		// delineate the client-errors (ie, bad config) from the fetch-errors. Client
		// errors cannot be retried until the developer fixes the calling context.
		try {

			// Extract options, with defaults, from config.
			var headers = ( config.headers || undefined );
			var method = ( config.method || null );
			var url = ( config.url || "" );
			var params = ( config.params ?? Object.create( null ) );
			var form = ( config.form ?? null );
			var json = ( config.json ?? null );
			var body = ( config.body ?? null );
			var signal = ( config.signal ?? null );

			// The fetch* variables are the values that we'll actually use to generate
			// the fetch() call. We're going to assign these based on the configuration
			// data that was passed-in.
			var fetchHeaders = new Headers( headers );
			var fetchContentType = fetchHeaders.get( "content-type" );
			var fetchMethod = null;
			var fetchUrl = this.buildUrl( url, params );
			var fetchBody = null;
			var fetchSignal = signal;

			if ( form ) {

				// NOTE: For form data posts, we want the browser to build the Content-
				// Type for us so that it puts in both the "multipart/form-data" plus the
				// correct, auto-generated field delimiter.
				fetchHeaders.delete( "content-type" );
				// ColdFusion will only parse the form data if the method is POST.
				fetchMethod = "post";
				fetchBody = this.buildFormData( form );

			} else if ( json ) {

				fetchHeaders.set( "content-type", ( fetchContentType || "application/json" ) );
				fetchMethod = ( method || "post" );
				fetchBody = JSON.stringify( json );

			} else if ( body ) {

				// When a body is going to be posted, we're going to allow fetch to infer
				// the content-type based on the payload.
				fetchMethod = ( method || "post" );
				fetchBody = body;

			} else {

				fetchMethod = ( method || "get" );

			}

			var fetchRequest = new window.Request(
				fetchUrl,
				{
					headers: fetchHeaders,
					method: fetchMethod.toUpperCase(),
					body: fetchBody,
					signal: fetchSignal
				}
			);

		} catch ( error ) {

			throw new ApiClientError({
				type: "ClientError",
				cause: error,
			});

		}

		// Now that we've made it through the config parsing and request setup phase, we
		// can perform a try/catch for the fetch and body reading. This reads the body as
		// an opaque blob - casting the blob to the appropriate type happens in the next
		// phase.
		try {

			var fetchResponse = await window.fetch( fetchRequest );
			var fetchBlob = await fetchResponse.blob();

		} catch ( error ) {

			var type = fetchSignal?.aborted
				? "AbortError"
				: "TransportError"
			;

			throw new ApiClientError({
				type,
				cause: error,
			});

		}

		// Now that we've made it through the network request and body consumption phase,
		// we need to cast the opaque blob into the appropriate data type. At this point,
		// the response content is fully loaded in memory; so any error that occurs here
		// is strictly a parsing error and not a transport error.
		try {

			var contentType = ( fetchResponse.headers.get( "content-type" ) ?? "" );
			var data = await this.parseResponseBody( contentType, fetchBlob );

		} catch ( error ) {

			throw new ApiClientError({
				type: "ParseError",
				status: fetchResponse.status,
				cause: error,
			});

		}

		if ( fetchResponse.ok ) {

			return data;

		}

		// The request came back with a non-2xx status code; but may still contain an
		// error structure that is defined by our business domain.
		// --
		// If the error data is an Object (which it should be if the server responded with
		// a domain-based error), then it should have "type" and "message" properties
		// within it. That said, just because this isn't a transport error, it doesn't
		// mean that this error is actually being returned by our application.
		if (
			( typeof( data?.type ) === "string" ) &&
			( typeof( data?.message ) === "string" )
			) {

			throw new ApiClientError({
				type: "DomainError",
				domainType: data.type,
				userMessage: data.message,
				status: fetchResponse.status,
			});

		}

		// If the error data has any other shape, it means that an unexpected error
		// occurred on the server (or somewhere in transit). Let's pass that raw data
		// through as the cause, using the default error structure.
		throw new ApiClientError({
			status: fetchResponse.status,
			cause: data,
		});

	}


	/**
	* I provide a proxy to the makeRequest() method and add retry mechanics. If the
	* request rejects with a retryable error, a back-off will be applied to retry the
	* request several times before giving up.
	*
	* GUARANTEE: All errors produced by this method will be instances of ApiClientError;
	* and will have a consistent structure even if they are low-level networking errors.
	*/
	async makeRequestWithRetry( config ) {

		// Rather than relying on the maths to do back-off calculations, this collection
		// provides an explicit set of back-off values (in milliseconds). This collection
		// also doubles as the number of attempts that we should execute against the
		// underlying makeRequest() method.
		// --
		// NOTE: Some randomness will be applied to these values as execution time.
		var backoffDurations = [
			200,
			700,
			1000,
			2000,
			4000,
			// 8000,
			// 16000,
			0, // Indicates that the last timeout should be recorded as an error.
		];

		// We're going to record the number of attempts into any error that bubbles-up.
		var attempts = 0;

		for ( var retryDelay of backoffDurations ) {

			attempts++;

			try {

				return await this.makeRequest( config );

			} catch ( error ) {

				// NOTE: In some cases, we would want to inspect the HTTP METHOD in order
				// to see if it is retryable (and perhaps only allow GET requests to be
				// retried). However, since this API Client has a separate method designed
				// specifically for retries, we are going to assume that the calling
				// context understands when it IS OK and IS NOT OK to retry. Therefore, we
				// are only going to look at the returned error as a means to to determine
				// if retry is the best next step.
				if ( ! retryDelay || ! this.isRetryableError( error ) ) {

					error.attempts = attempts;
					throw error;

				}

				console.warn( `API request failed, retrying in ${ retryDelay }ms.` );

				await this.blockAndWait( this.applyJitter( retryDelay ), config?.signal );

			}

		}

	}

	// ---
	// PRIVATE METHODS.
	// ---

	/**
	* I apply a +/- 20% offset to the current value (a small attempt to prevent the
	* stampeding herd problem).
	*/
	applyJitter( value ) {

		// Generate a +/- 20% delta from the original value.
		var percentJitter = ( ( 20 - Math.floor( Math.random() * 40 ) ) / 100 );
		var jitter = ( value * percentJitter );

		return Math.floor( value + jitter );

	}


	/**
	* I return a Promise that is resolved after the given timeout. The internal timer can
	* be canceled with an optional AbortSignal.
	*
	* Note: if the underlying wait is aborted via the signal, the returned Promise will
	* resolve immediately. This should NOT be a problem for the calling context because
	* the subsequent retry-fetch will short-circuit and throw an error due to the reuse of
	* the already-aborted signal.
	*/
	blockAndWait( durationInMilliseconds, signal = undefined ) {

		var promise = new Promise(
			( resolve ) => {

				// When the calling context triggers an abort, we need to listen for it so
				// that we can turn around and clear the internal timer.
				// --
				// NOTE: We're creating a proxy callback for our resolve function in order
				// to remove this event-listener once the timer executes. This way, our
				// event-handler never gets invoked if there's nothing for it to actually
				// do. Also note that the "abort" event will only ever get emitted once,
				// regardless of how many times the calling context tries to invoke
				// .abort() on its AbortController.
				signal?.addEventListener( "abort", handleAbort );

				// Setup our internal timer that we can clear-on-abort.
				var internalTimer = setTimeout( internalResolve, durationInMilliseconds );

				// -- Internal methods. -- //

				function internalResolve() {

					signal?.removeEventListener( "abort", handleAbort );
					resolve( durationInMilliseconds );

				}

				function handleAbort() {

					clearTimeout( internalTimer );
					resolve( -1 );

				}

			}
		);

		return promise;

	}


	/**
	* I build a FormData instance from the given object.
	*
	* NOTE: At this time, only simple values (ie, no files) are supported.
	*/
	buildFormData( formFields ) {

		var formData = new FormData();

		Object.entries( formFields ).forEach(
			( [ key, value ] ) => {

				formData.append( key, value );

			}
		);

		return formData;

	}


	/**
	* I build the URL for the fetch request by merging any arbitrary search parameters
	* into the already formed URL. Parameter collisions give precedence to the passed-in
	* params object.
	*/
	buildUrl( url, params ) {

		if ( ! url ) {

			throw new Error( "Url is empty" );

		}

		var fetchUrl = new URL( url, window.location.href );

		for ( var [ key, value ] of Object.entries( params ) ) {

			fetchUrl.searchParams.set( key, value );

		}

		return fetchUrl;

	}


	/**
	* I try to determine if the given request-error object indicates that the original
	* request can be retried.
	*/
	isRetryableError( error ) {

		// If the configuration is bad, retrying would only yield the same failure. The
		// developer has to fix the bug in the calling context.
		if ( error.type === "ClientError" ) {

			return false;

		}

		// If the error was triggered by an explicit Abort, we don't want to retry it -
		// it was terminated early on purpose by the calling context.
		if ( error.type === "AbortError" ) {

			return false;

		}

		return (
			( error.status === 0 )   || // Various network failures.
			( error.status === 408 ) || // Request timeout.
			( error.status === 429 ) || // Too many requests (ie, rate limiting).
			( error.status >= 500 )     // Basically all unexpected server errors.
		);

	}


	/**
	* I cast the given response binary into the appropriate data type based on the HTTP
	* response content-type header. Any parsing error that occurs is allowed to bubble up
	* to the calling context.
	*/
	async parseResponseBody( contentType, blob ) {

		if ( RE_CONTENT_TYPE_JSON.test( contentType ) ) {

			var text = await blob.text();

			if ( ! text ) {

				return null;

			}

			return JSON.parse( text );

		}

		if ( RE_CONTENT_TYPE_TEXT.test( contentType ) ) {

			return await blob.text();

		}

		if ( ! blob.size ) {

			return null;

		}

		return blob;

	}

}
